import * as React from "react";
import { Button, Hint, Input } from "react-ui";

import { TextOverflowHint } from "../../../Internals/TextOverflowHint/TextOverflowHint";
import { Tooltip } from "../../../Internals/Tooltip/Tooltip";
import { getHintWidth } from "../../../constants";
import { nullElement } from "../../../TypingUtils";
import {
    PicklistColumnMeta,
    PicklistDataItem,
    PicklistQueryResponse,
} from "../../../../Engine/Core/Picklists/PicklistTypes";
import { ValueEditorsFocusManager } from "../../../../Helpers/JumpByEnterOnNextControl/ValueEditorsFocusManager";
import { InternalIcon } from "../../../Icons/Icon";
import { GroupButtonContainer, Root } from "../Common/Styles/Picklist.styles";
import { getPickListItemsByValue } from "../Common/PicklistItemProvider";

import { BasePicklistProps } from "./BasePicklistProps";
import { PicklistModal } from "./PicklistModal/PicklistModal";
import { BasePicklistHint } from "./BasePicklistHint";

interface BasePicklistState {
    opened?: boolean;
    value?: string;
    columnMetas?: PicklistColumnMeta[];
}

export class BasePicklist extends React.Component<BasePicklistProps, BasePicklistState> {
    public static defaultProps = {
        isTooltipEnabled: true,
        columnsWidth: [150, 500],
        fields: ["code", "name"],
        overwriteOnInit: false,
        placeholder: "Начните вводить код или наименование",
        type: "group",
        empty: "Не заполнено",
        width: "100%",
        normativeHelp: [],
        hintFields: ["name"],
    };

    public state: BasePicklistState = {};

    public componentDidMount(): void {
        if (this.props.overwriteOnInit) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.changeValue(this.getValue());
        }
    }

    public render(): JSX.Element {
        if (this.props.type === "group" && this.props.width === "100%") {
            /* NOTE
             * Ширина контрола изначально работала неверно: ширина проставляется в input, и контрол получается шире.
             * Для 100% ширины, получается, что кнопка переносится на другую строку. Чтобы не сломать ширину во всех форма
             * этот косяк не починен, но обработана ситуация для 100% ширины
             */
            return (
                <Root group width={this.props.width}>
                    {this.renderInput()}
                    <GroupButtonContainer>
                        <Button
                            align={this.props.align}
                            data-tid={this.props["data-tid"]}
                            data-focus="focus"
                            narrow={this.props.narrowButton}
                            corners={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            onClick={this.handleOpenModal}
                            disabled={this.props.disabled}>
                            <InternalIcon name="UiMenuDots3HIcon" size={14} />
                        </Button>
                    </GroupButtonContainer>
                    {this.renderModal()}
                </Root>
            );
        }
        return (
            <Root>
                {this.props.type === "group" ? (
                    <>
                        {this.renderInput()}
                        <GroupButtonContainer>
                            <Button
                                data-focus="focus"
                                data-tid={this.props["data-tid"]}
                                align={this.props.align}
                                narrow={this.props.narrowButton}
                                corners={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                onClick={this.handleOpenModal}
                                disabled={this.props.disabled}>
                                <InternalIcon name="UiMenuDots3HIcon" size={14} />
                            </Button>
                        </GroupButtonContainer>
                    </>
                ) : (
                    this.renderTypeButton()
                )}
                {this.renderModal()}
            </Root>
        );
    }

    private renderModal(): JSX.Element | null {
        if (!this.state.opened) {
            return nullElement;
        }
        return (
            <PicklistModal
                columns={this.props.fields.map((field, i) => ({
                    fieldName: field,
                    width: this.props.columnsWidth[i] ?? 0,
                    header: this.props.headers?.[i],
                    markdown: this.state.columnMetas?.find(x => x.name === field)?.withMarkdown ?? false,
                }))}
                multiple={this.props.multiple}
                normativeHelp={this.props.normativeHelp}
                placeholder={this.props.placeholder}
                query={this.executeQuery}
                querySelectedValues={this.findSelectedValues}
                searchByField={this.props.searchByField}
                searchFields={this.props.searchFields}
                title={this.props.title}
                value={this.getValue()}
                partialHelpTip={this.props.partialHelpTip}
                footer={this.props.footer}
                onChange={this.handleModalChange}
                onClose={this.handleCloseModal}
                descriptionInModal={this.props.descriptionInModal}
                isEnableCaseIndependentValidation={this.props.isEnableCaseIndependentValidation}
            />
        );
    }

    private readonly handleModalChange = (items: PicklistDataItem[]) => {
        this.props.handleModalChange(items);
        this.setState({ opened: false });
    };

    private readonly handleOpenModal = ({ currentTarget }: React.MouseEvent<HTMLElement>) => {
        if (this.props.onModalOpen !== undefined) {
            this.props.onModalOpen();
        }
        this.setState({ opened: true });
        //для kind=button
        if (currentTarget.localName === "button") {
            ValueEditorsFocusManager.setCurrentValueEditorOrder(currentTarget);
        }
    };

    private readonly handleCloseModal = () => {
        this.setState({ opened: false });
    };

    private getValue(): string {
        return this.state.value === undefined ? this.props.value : this.state.value;
    }

    public readonly handleInputChange = (value: string): Promise<void> => {
        if (this.props.handleInputValueSave !== undefined) {
            this.props.handleInputValueSave(this.props.value);
        }
        this.setState({ value: value });
        return this.changeValue(value);
    };

    public readonly handleOnPaste = (event: React.ClipboardEvent<unknown>): void => {
        if (this.props.onPaste != undefined) {
            const pasteWasIntercept = this.props.onPaste(event);
            if (pasteWasIntercept) {
                this.setState({ value: undefined });
            }
        }
    };

    public readonly handleOnBlur = async (): Promise<void> => {
        if (this.state.value !== undefined) {
            if (this.props.handleInputValueChange) {
                this.props.handleInputValueChange(this.state.value);
            }
            await this.changeValue(this.state.value);
            this.setState({ value: undefined });
        }
    };

    public readonly changeValue = async (value: string): Promise<void> => {
        await this.props
            .query(value)
            .then(picklistQueryResponse => this.props.onChange(value, picklistQueryResponse))
            .catch(e => {
                this.props.onChange(value, { items: [], suitableCount: 0, columnMetas: [] });
                // eslint-disable-next-line no-console
                console.error("Picklist request failed", e);
            });
    };

    private renderTypeButton(): JSX.Element {
        const { error, warning, description, isTooltipEnabled } = this.props;
        const tooltipMessage = error || warning;
        const hintMaxWidth = getHintWidth(description?.length ?? 0);

        const tooltipTrigger = tooltipMessage && isTooltipEnabled ? "hover" : "closed";
        const tooltipPosition = this.props.tooltipPosition || "top center";
        const value = this.getValue();

        const button = (
            <Button
                data-focus="focus"
                data-tid={this.props["data-tid"]}
                error={Boolean(this.props.error)}
                narrow={this.props.narrowButton}
                warning={Boolean(this.props.warning)}
                width={this.props.width}
                align={this.props.align}
                onClick={this.handleOpenModal}
                disabled={this.props.disabled}>
                {value || this.props.empty}
            </Button>
        );

        return (
            <Tooltip trigger={tooltipTrigger} value={tooltipMessage} pos={tooltipPosition}>
                {this.props.description ? (
                    <Hint maxWidth={hintMaxWidth} text={this.props.description}>
                        {button}
                    </Hint>
                ) : (
                    <BasePicklistHint
                        {...this.props}
                        value={value}
                        columnMetas={this.state.columnMetas}
                        hintWidth={this.props.hintWidth}
                        hintOpened={tooltipTrigger === "closed"}
                        isEnableCaseIndependentValidation={this.props.isEnableCaseIndependentValidation}>
                        {button}
                    </BasePicklistHint>
                )}
            </Tooltip>
        );
    }

    private renderInput(): JSX.Element {
        const value = this.getValue();
        const { error, warning, description, isTooltipEnabled, width } = this.props;
        const errorText = this.state.value === undefined ? error || warning : undefined;
        const hasErrorOrWarning = error || warning;
        const tooltipMessage = this.state.value === undefined ? hasErrorOrWarning : undefined;
        const tooltipTrigger =
            this.state.value === undefined && errorText && isTooltipEnabled ? "hover&focus" : "closed";
        const tooltipPosition = this.props.tooltipPosition ?? "top center";

        const keyDownHandler = (event: React.KeyboardEvent) => {
            if (event.key === "Enter") {
                ValueEditorsFocusManager.focusOnNextValueEditor(event.target);
            }
        };

        const renderRawInput = () => (
            <Input
                onPaste={this.handleOnPaste}
                onKeyDown={keyDownHandler}
                align={this.props.align}
                error={Boolean(error) && this.state.value === undefined}
                value={value}
                warning={Boolean(warning) && this.state.value === undefined}
                width={width}
                onBlur={this.handleOnBlur}
                onValueChange={this.handleInputChange}
                disabled={this.props.disabled}
            />
        );

        if (this.props.multiple) {
            const showTextOverflowHint = Boolean(!description && !hasErrorOrWarning);

            return (
                <Tooltip trigger={tooltipTrigger} value={tooltipMessage} pos={tooltipPosition}>
                    <TextOverflowHint showHint={showTextOverflowHint} text={value}>
                        {renderRawInput()}
                    </TextOverflowHint>
                </Tooltip>
            );
        }

        const picklistHintOpened = tooltipTrigger === "closed" && !hasErrorOrWarning;

        return (
            <Tooltip trigger={tooltipTrigger} value={tooltipMessage} pos={tooltipPosition}>
                <BasePicklistHint
                    {...this.props}
                    value={value}
                    columnMetas={this.state.columnMetas}
                    hintWidth={this.props.hintWidth}
                    hintOpened={picklistHintOpened}
                    isEnableCaseIndependentValidation={this.props.isEnableCaseIndependentValidation}>
                    {renderRawInput()}
                </BasePicklistHint>
            </Tooltip>
        );
    }

    private readonly executeQuery = async (
        value?: string
    ): Promise<{ items: PicklistDataItem[]; suitableCount: number }> => {
        const result = await this.props.query(value);
        if (this.state.columnMetas == undefined || this.state.columnMetas.length === 0) {
            this.setState({ columnMetas: result.columnMetas });
        }
        return result;
    };

    private readonly findSelectedValues = async (values: string): Promise<PicklistQueryResponse> =>
        getPickListItemsByValue(values, this.props, this.props.isEnableCaseIndependentValidation, false);
}
